import { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, BACKEND } from "@/lib/api";

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/uploads", fd);
  return data.url;
};

const Btn = ({ on, active, label, children, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} title={label} aria-label={label} data-testid={testid}
    className={`rounded-lg p-2 transition-colors ${active ? "bg-violet-100 text-violet-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
    {children}
  </button>
);

export const RichEditor = ({ initial, onChange }) => {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false } }), Image],
    content: (initial || "").replace(/src="\/api\//g, `src="${BACKEND}/api/`),
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: { attributes: { class: "prose-pd min-h-[420px] px-6 py-6 outline-none", "data-testid": "rich-editor-content" } },
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (!url) c().unsetLink().run();
    else c().extendMarkRange("link").setLink({ href: url }).run();
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = await uploadImage(file);
      c().setImage({ src: `${BACKEND}${url}`, alt: file.name.replace(/\.[^.]+$/, "") }).run();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="rich-editor">
      <div className="sticky top-16 z-10 flex flex-wrap items-center gap-0.5 rounded-t-2xl border-b border-slate-200 bg-white/95 p-2 backdrop-blur">
        <Btn label="Heading 2" testid="editor-h2" active={editor.isActive("heading", { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
        <Btn label="Heading 3" testid="editor-h3" active={editor.isActive("heading", { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn label="Bold" testid="editor-bold" active={editor.isActive("bold")} on={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn label="Italic" testid="editor-italic" active={editor.isActive("italic")} on={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn label="Link" testid="editor-link" active={editor.isActive("link")} on={setLink}><Link2 className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn label="Bullet list" testid="editor-bullets" active={editor.isActive("bulletList")} on={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn label="Numbered list" testid="editor-numbers" active={editor.isActive("orderedList")} on={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn label="Quote" testid="editor-quote" active={editor.isActive("blockquote")} on={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <Btn label="Divider" testid="editor-divider" on={() => c().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Btn>
        <Btn label="Insert image" testid="editor-image" on={() => fileRef.current?.click()}><ImagePlus className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn label="Undo" testid="editor-undo" on={() => c().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
        <Btn label="Redo" testid="editor-redo" on={() => c().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickImage} data-testid="editor-image-input" />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};
